import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useDispatch, useSelector } from "react-redux";
import { fetchSessionCategories } from "../../store/slices/sessionCategorySlice";
import "bootstrap/dist/css/bootstrap.min.css";
import "remixicon/fonts/remixicon.css";

const SessionCategoryList = ({ onAddSessionCategory, onEditSessionCategory, onDeleteSessionCategory }) => {
    const dispatch = useDispatch();
    const { sessionCategories, isLoading, error } = useSelector((state) => state.sessionCategories);

    const [filterText, setFilterText] = useState("");
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        dispatch(fetchSessionCategories());
    }, [dispatch]);

    const filteredCategories = (sessionCategories || []).filter((c) =>
        Object.values(c || {}).some((v) =>
            String(v || "").toLowerCase().includes(filterText.toLowerCase())
        )
    );

    const totalRows = filteredCategories.length;
    const start = (currentPage - 1) * rowsPerPage + 1;
    const end = Math.min(currentPage * rowsPerPage, totalRows);

    const CustomPagination = ({ onChangePage, onChangeRowsPerPage, currentPage, rowsPerPage, rowCount }) => {
        const totalPages = Math.ceil(rowCount / rowsPerPage);

        return (
            <div className="d-flex justify-content-between align-items-center p-3 border-top">
                <div className="d-flex align-items-center gap-2">
                    <span className="text-muted" style={{ fontSize: "0.875rem" }}>
                        Rows per page:
                    </span>
                    <select
                        className="form-select form-select-sm"
                        style={{ width: "80px", fontSize: "0.875rem" }}
                        value={rowsPerPage}
                        onChange={(e) =>
                            onChangeRowsPerPage(Number(e.target.value), currentPage)
                        }
                    >
                        {[5, 10, 15, 20, 25].map((option) => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="d-flex align-items-center gap-1">
                    <button
                        className="btn btn-sm border"
                        onClick={() => onChangePage(currentPage - 1, rowCount)}
                        disabled={currentPage === 1}
                    >
                        <i className="ri-arrow-left-s-line"></i>
                    </button>

                    <span className="mx-3 text-muted" style={{ fontSize: "0.875rem" }}>
                        Page {currentPage} of {totalPages}
                    </span>

                    <button
                        className="btn btn-sm border"
                        onClick={() => onChangePage(currentPage + 1, rowCount)}
                        disabled={currentPage === totalPages}
                    >
                        <i className="ri-arrow-right-s-line"></i>
                    </button>
                </div>
            </div>
        );
    };

    const columns = [
        {
            name: "S.No",
            cell: (row, index) => index + 1 + (currentPage - 1) * rowsPerPage,
            width: "80px",
        },
        {
            name: "Name",
            selector: (row) => row.name || "",
            sortable: true,
        },
        {
            name: "Status",
            selector: (row) => row.status || "active",
            sortable: true,
            cell: (row) => (
                <span className={`badge bg-label-${row.status === "active" ? "success" : "secondary"}`}>
                    {row.status ? row.status.toUpperCase() : "ACTIVE"}
                </span>
            ),
        },
        {
            name: "Created By",
            selector: (row) => row.creator?.name || "N/A",
            sortable: true,
        },
        {
            name: "Updated By",
            selector: (row) => row.updater?.name || "N/A",
            sortable: true,
        },
        {
            name: "Actions",
            cell: (row) => (
                <div className="d-flex justify-content-center gap-1">
                    {onEditSessionCategory && (
                        <button
                            className="btn btn-sm btn-outline-primary"
                            title="Edit Session Category"
                            onClick={() => onEditSessionCategory(row.id)}
                        >
                            <i className="ri-edit-line"></i>
                        </button>
                    )}
                    {onDeleteSessionCategory && (
                        <button
                            className="btn btn-sm btn-outline-danger"
                            title="Delete Session Category"
                            onClick={() => onDeleteSessionCategory(row.id)}
                        >
                            <i className="ri-delete-bin-line"></i>
                        </button>
                    )}
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: "120px",
        },
    ];

    const handleExportPDF = () => {
        const doc = new jsPDF();
        doc.text("Session Categories List", 14, 10);
        const tableColumn = ["S.No", "Name", "Status", "Created By", "Updated By"];
        const tableRows = [];

        filteredCategories.forEach((cat, index) => {
            tableRows.push([
                index + 1,
                cat.name || "",
                cat.status ? cat.status.toUpperCase() : "ACTIVE",
                cat.creator?.name || "N/A",
                cat.updater?.name || "N/A",
            ]);
        });

        autoTable(doc, {
            head: [tableColumn],
            body: tableRows,
            startY: 20,
        });

        doc.save("session_categories_list.pdf");
    };

    const handleCopy = () => {
        const copyText = filteredCategories
            .map((cat, index) => `${index + 1}\t${cat.name}\t${cat.status || "active"}\t${cat.creator?.name || "N/A"}\t${cat.updater?.name || "N/A"}`)
            .join("\n");

        navigator.clipboard.writeText(copyText);

        alert("Copied successfully!");
    };

    const handlePrint = () => {
        const printContent = `
      <html>
        <head>
          <title>Session Categories List</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ccc; padding: 8px; text-align: left; }
            th { background-color: #f8f9fa; }
          </style>
        </head>
        <body>
          <h3>Session Categories List</h3>
          <table>
            <thead>
              <tr>
                <th>S.No</th>
                <th>Name</th>
                <th>Status</th>
                <th>Created By</th>
                <th>Updated By</th>
              </tr>
            </thead>
            <tbody>
              ${filteredCategories
                .map(
                    (cat, index) => `
                <tr>
                  <td>${index + 1}</td>
                  <td>${cat.name || ""}</td>
                  <td>${cat.status ? cat.status.toUpperCase() : "ACTIVE"}</td>
                  <td>${cat.creator?.name || "N/A"}</td>
                  <td>${cat.updater?.name || "N/A"}</td>
                </tr>`
                )
                .join("")}
            </tbody>
          </table>
        </body>
      </html>
    `;

        const printWindow = window.open("", "_blank");
        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.print();
    };

    return (
        <div>
            <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2 px-0 pt-0">
                <div className="d-flex align-items-center gap-2 position-relative">
                    <div className="dropdown">
                        <button
                            className="btn btn-label-primary btn-sm dropdown-toggle"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                        >
                            <i className="ri-download-2-line me-1"></i> Export
                        </button>
                        <ul className="dropdown-menu shadow-sm">
                            <li>
                                <button className="dropdown-item" onClick={handleExportPDF}>
                                    <i className="ri-file-pdf-line me-1 text-danger"></i> PDF
                                </button>
                            </li>
                            <li>
                                <CSVLink
                                    data={filteredCategories.map((c, i) => ({
                                        "S.No": i + 1,
                                        Name: c.name,
                                        Status: c.status,
                                        "Created By": c.creator?.name || "N/A",
                                        "Updated By": c.updater?.name || "N/A",
                                    }))}
                                    filename="session_categories_export.csv"
                                    className="dropdown-item"
                                >
                                    <i className="ri-file-excel-line me-1 text-success"></i> CSV
                                </CSVLink>
                            </li>
                            <li>
                                <button className="dropdown-item" onClick={handleCopy}>
                                    <i className="ri-file-copy-line me-1"></i> Copy
                                </button>
                            </li>
                            <li>
                                <button className="dropdown-item" onClick={handlePrint}>
                                    <i className="ri-printer-line me-1"></i> Print
                                </button>
                            </li>
                        </ul>
                    </div>

                    <input
                        type="text"
                        placeholder="Search category..."
                        className="form-control form-control-sm"
                        style={{ width: "200px" }}
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                    />
                </div>

                <h6 className="mb-0 text-center text-muted flex-grow-1">
                    Showing {start}-{end} of {totalRows} session categories
                </h6>

                <div>
                    {onAddSessionCategory && (
                        <button
                            className="btn btn-sm btn-primary"
                            title="Add Session Category"
                            onClick={onAddSessionCategory}
                        >
                            <i className="ri-add-line me-1"></i>
                            Add Session Category
                        </button>
                    )}
                </div>
            </div>

            <div className="card-body p-0 mt-3">
                {error && <div className="alert alert-danger">{error}</div>}
                <DataTable
                    columns={columns}
                    data={filteredCategories}
                    pagination
                    paginationPerPage={rowsPerPage}
                    paginationTotalRows={totalRows}
                    onChangeRowsPerPage={setRowsPerPage}
                    onChangePage={setCurrentPage}
                    paginationComponent={CustomPagination}
                    highlightOnHover
                    striped
                    responsive
                    noDataComponent={<div className="p-3 text-muted">No session categories found</div>}
                    progressPending={isLoading}
                />
            </div>
        </div>
    );
};

export default SessionCategoryList;
